import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import type { Location, NavigateFunction } from 'react-router-dom';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Divider,
  Link,
} from '@mui/material';
import { useTeacher } from '../context/TeacherContext';
import apiAxiosInstance from '../utils/axiosInstance';

interface LocationState {
  from?: {
    pathname: string;
  };
}

const Login: React.FC = () => {
  const [formData, setFormData] = useState({ email: "", password: "" });
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { setTeacher } = useTeacher();
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const clearAuthData = () => {
    localStorage.removeItem('token');
    setTeacher(null);
  };

  useEffect(() => {
    clearAuthData();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setErrorMessage(null);

    try {
      clearAuthData();
      
      const response = await apiAxiosInstance.post('/auth/login', formData);

      if (response.data.token && response.data.teacher) {
        localStorage.setItem('token', response.data.token);
        setTeacher(response.data.teacher);

        const from = location.state?.from?.pathname ||
                    (window.location.pathname !== '/login' ? window.location.pathname : '/edu-chat');
        navigate(from, { replace: true });
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (err: any) {
      console.error('Login error:', err);
      setErrorMessage(err.response?.data?.message || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    const error = params.get('error');
    
    if (error) {
      setErrorMessage('Authentication failed');
      return;
    }
    
    if (token) {
      localStorage.setItem('token', token);
      
      const fetchUser = async () => {
        try {
          const response = await apiAxiosInstance.get('/auth/verify');
          if (response.data.teacher) {
            setTeacher(response.data.teacher);
            const redirectPath = sessionStorage.getItem('redirectPath') || '/edu-chat';
            sessionStorage.removeItem('redirectPath');
            navigate(redirectPath, { replace: true });
          }
        } catch (error) {
          console.error('Error fetching user data:', error);
          setErrorMessage('Error authenticating with provider');
        }
      };

      fetchUser();
    }
  }, [navigate, setTeacher]);

  const handleGoogleLogin = () => {
    sessionStorage.setItem('redirectPath', location.state?.from?.pathname || '/edu-chat');
    const authUrl = process.env.NODE_ENV === 'production' 
      ? (process.env.REACT_APP_AUTH_URL || 'https://teach-ai-db-backend.vercel.app/auth')
      : (process.env.REACT_APP_AUTH_URL || 'http://localhost:5000/auth');
    window.location.href = `${authUrl}/google`;
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f5f5f5',
        p: 2,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          p: { xs: 3, sm: 4 },
          width: '100%',
          maxWidth: '420px',
          borderRadius: 3,
          backgroundColor: '#fff',
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
        }}
      >
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Typography 
            variant="h5" 
            component="h1" 
            color="primary"
            fontWeight="600"
            sx={{ mb: 0.5 }}
          >
            Welcome to TeachAssist
          </Typography>
          <Typography 
            variant="body2" 
            color="text.secondary"
          >
            Create AI-powered lesson plans in minutes
          </Typography>
        </Box>

        {/* Google Sign In */}
        <Button
          variant="outlined"
          onClick={handleGoogleLogin}
          fullWidth
          sx={{ 
            height: '48px',
            borderRadius: 2,
            borderColor: '#dadce0',
            color: '#3c4043',
            textTransform: 'none',
            fontSize: '0.95rem',
            fontWeight: 500,
            mb: 2.5,
            '&:hover': {
              borderColor: '#d2e3fc',
              backgroundColor: 'rgba(66, 133, 244, 0.04)',
            },
          }}
        >
          <Box
            component="img"
            src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
            alt="Google"
            sx={{ width: 20, height: 20, mr: 1.5 }}
          />
          Sign in with Google
        </Button>

        {/* Divider */}
        <Box sx={{ position: 'relative', my: 2.5 }}>
          <Divider />
          <Typography
            variant="body2"
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              backgroundColor: '#fff',
              px: 1.5,
              color: 'text.secondary',
              fontSize: '0.85rem',
            }}
          >
            Or continue with email
          </Typography>
        </Box>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label="Email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            required
            autoComplete="email"
            sx={{ 
              mb: 2,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                backgroundColor: '#f8f9ff',
              },
            }}
          />

          <TextField
            fullWidth
            label="Password"
            name="password"
            type="password"
            value={formData.password}
            onChange={handleChange}
            required
            autoComplete="current-password"
            sx={{ 
              mb: 2,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                backgroundColor: '#f8f9ff',
              },
            }}
          />

          {errorMessage && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
              {errorMessage}
            </Alert>
          )}

          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={loading}
            sx={{ 
              height: '48px',
              borderRadius: 2,
              fontSize: '1rem',
              fontWeight: 600,
              textTransform: 'none',
              background: 'linear-gradient(90deg, #1976d2 0%, #ff7043 100%)',
              '&:hover': {
                background: 'linear-gradient(90deg, #1565c0 0%, #f4511e 100%)',
              },
            }}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : 'Sign In'}
          </Button>
        </form>

        {/* Sign Up Link */}
        <Box sx={{ textAlign: 'center', mt: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Don't have an account?
          </Typography>
          <Button
            variant="outlined"
            onClick={() => navigate('/signup')}
            sx={{ 
              textTransform: 'none',
              fontSize: '0.95rem',
              fontWeight: 500,
              borderRadius: 2,
              px: 4,
            }}
          >
            Sign Up
          </Button>
        </Box>

        {/* Footer */}
        <Typography 
          variant="caption" 
          color="text.secondary"
          sx={{ 
            display: 'block', 
            textAlign: 'center', 
            mt: 3,
            fontSize: '0.75rem',
          }}
        >
          By continuing, you agree to our{' '}
          <Link 
            href="/terms-of-service" 
            underline="hover"
            sx={{ cursor: 'pointer' }}
          >
            Terms of Service
          </Link>
          {' '}and{' '}
          <Link 
            href="/privacy-policy" 
            underline="hover"
            sx={{ cursor: 'pointer' }}
          >
            Privacy Policy
          </Link>
        </Typography>
      </Paper>
    </Box>
  );
};

export default Login;
